<template>
  <div class="shadow-card rounded-lg bg-white px-4 py-6 md:px-6 md:py-8">
    <form @submit.prevent="handleSubmit">
      <RichTextEditor
        id="about_us"
        v-model:value="values.about_us"
        v-model:errors="errors.about_us"
        :label="__('About company')"
        :placeholder="
          __(
            'Here you can add more details about the company in general, company culture, ways of working.'
          )
        "
      />

      <div class="mt-8 grid gap-4 md:grid-cols-2 md:gap-y-8">
        <InputMultiSelect
          id="benefits"
          v-model:values="values.benefits"
          v-model:errors="errors.benefits"
          :label="__('Benefits')"
          :placeholder="__('Company benefits')"
          :options="options.benefits"
        >
          <template #icon>
            <Icon class="text-lg text-neutral-400" name="loyalty" is-filled />
          </template>
        </InputMultiSelect>

        <InputMultiSelect
          id="values"
          v-model:values="values.values"
          v-model:errors="errors.values"
          :label="__('Values')"
          :placeholder="__('Company values')"
          :options="options.values"
        >
          <template #icon>
            <Icon class="text-lg text-neutral-400" name="favorite" is-filled />
          </template>
        </InputMultiSelect>
      </div>

      <span
        v-if="errorMessage"
        class="mt-8 block text-center text-sm text-error"
      >
        {{ errorMessage }}
      </span>

      <div
        class="mt-8 flex flex-col gap-2 md:flex-row-reverse md:justify-center"
      >
        <Button
          class="flex items-center justify-center gap-1.5"
          :type="isLoading ? 'button' : 'submit'"
          :disabled="isLoading"
        >
          <Icon
            v-if="isLoading"
            class="animate-spin text-lg"
            name="progress_activity"
          />
          <span>{{ isLoading ? __('Loading...') : __('Save') }}</span>
        </Button>

        <Button variant="ghost" @click="handleCancelButtonClick">
          <span>{{ __('Cancel') }}</span>
        </Button>
      </div>
    </form>
  </div>
</template>

<script setup>
  import { ref } from 'vue';
  import { useToast } from 'vue-toastification';
  import { __ } from '@/helpers';
  import { Button } from '@/components/block/button';
  import { Icon } from '@/components/block/icon';
  import { InputLocation } from '@/components/form/input-location';
  import { InputMultiSelect } from '@/components/form/input-multi-select';
  import { InputSelect } from '@/components/form/input-select';
  import { InputText } from '@/components/form/input-text';
  import { RichTextEditor } from '@/components/form/rich-text-editor';

  const props = defineProps({
    data: {
      type: Object,
      required: true
    },
    options: {
      type: Object,
      required: true
    },
    errorMessage: {
      type: String,
      default: ''
    }
  });

  const emits = defineEmits(['save', 'cancel']);

  const toast = useToast();

  const data = ref(JSON.parse(JSON.stringify(props.data)));
  const values = ref({
    about_us: data.value.about_us || '',
    benefits: data.value.benefits || [],
    values: data.value.values || []
  });
  const errors = ref({
    about_us: [],
    benefits: [],
    values: []
  });
  const isLoading = ref(false);

  function handleVisibilityButtonClick(key) {
    values.value[key].is_visible = !values.value[key].is_visible;
  }

  function handleCancelButtonClick() {
    emits('cancel');
  }

  async function handleSubmit() {
    if (Object.values(errors.value).some((error) => error.length)) {
      if (firstElement) {
        firstElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      return;
    }

    isLoading.value = true;

    try {
      const response = await axios.post(
        route('redesign.company.update.about'),
        values.value
      );

      if (response?.data?.message) {
        toast.success(response.data.message);
      }

      emits('save', response.data.data);
    } catch (error) {
      if (error?.response?.data?.errors) {
        errors.value = { ...errors.value, ...error.response.data.errors };
      } else {
        toast.error(
          __('An unexpected error has occured. Please try again later.')
        );
      }
    }

    isLoading.value = false;
  }
</script>
